<template>
  <v-card
    class="section-card glass-card sticky-summary booking-summary"
    elevation="0"
  >
    <v-card-title class="booking-summary__title">
      <div class="booking-summary__title-text">
        <span class="booking-summary__eyebrow">Etapa atual</span>
        <span>Resumo da Reserva</span>
      </div>
      <v-avatar
        size="40"
        class="booking-summary__avatar"
        tile
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            :d="summaryIcon"
            fill="currentColor"
          />
        </svg>
      </v-avatar>
    </v-card-title>
    <v-divider></v-divider>
    <v-card-text class="booking-summary__body">
      <div class="booking-summary__total">
        <div>
          <span class="summary-label">Valor total estimado</span>
          <div class="booking-summary__amount">
            {{ formatCurrency(totalPrice) }}
          </div>
        </div>
        <v-chip
          small
          class="booking-summary__chip"
        >
          {{ nightsLabel }}
        </v-chip>
      </div>

      <div class="booking-summary__grid">
        <div class="booking-summary__item">
          <span class="summary-label">
            <v-icon
              small
              left
            >mdi-calendar-check</v-icon>
            Check-in
          </span>
          <span class="summary-value">{{ formattedCheckIn }}</span>
        </div>
        <div class="booking-summary__item">
          <span class="summary-label">
            <v-icon
              small
              left
            >mdi-calendar-remove</v-icon>
            Check-out
          </span>
          <span class="summary-value">{{ formattedCheckOut }}</span>
        </div>
        <div class="booking-summary__item">
          <span class="summary-label">
            <v-icon
              small
              left
            >mdi-bed</v-icon>
            Categoria
          </span>
          <span class="summary-value">
            {{ selectedCategory ? selectedCategory.name : 'Selecione uma categoria' }}
          </span>
        </div>
        <div class="booking-summary__item">
          <span class="summary-label">
            <v-icon
              small
              left
            >mdi-account-multiple</v-icon>
            Hóspedes
          </span>
          <span class="summary-value">{{ guestsLabel }}</span>
        </div>
      </div>

      <div
        v-if="selectedCategory && selectedCategory.require_payment"
        class="booking-summary__notice"
      >
        <div>
          <span class="summary-label">{{ advancePaymentLabel }}</span>
          <div class="booking-summary__amount booking-summary__amount--small">
            {{ formatCurrency(selectedCategory.advance_payment) }}
          </div>
        </div>
        <v-chip
          color="success"
          text-color="white"
          small
        >
          <v-icon
            small
            left
          >
            mdi-shield-check
          </v-icon>
          Pagamento seguro
        </v-chip>
      </div>

      <div
        v-if="paymentMethod === 'cartao' && selectedInstallments > 1"
        class="booking-summary__installments"
      >
        <v-chip
          small
          outlined
          color="primary"
        >
          <v-icon
            small
            left
          >
            mdi-credit-card-outline
          </v-icon>
          {{ selectedInstallments }}x de {{ formatCurrency(installmentValue) }}
        </v-chip>
      </div>

      <v-alert
        v-if="holdActive"
        type="info"
        outlined
        dense
        class="mt-3"
      >
        Pagamento obrigatório em <strong>{{ holdCountdownLabel }}</strong>.
      </v-alert>
      <v-alert
        v-else-if="holdExpired && holdToken"
        type="error"
        outlined
        dense
        class="mt-3"
      >
        O tempo para finalizar esta reserva expirou.
      </v-alert>
    </v-card-text>
    <div
      v-if="$slots.footer"
      class="booking-summary__footer"
    >
      <slot name="footer"></slot>
    </div>
  </v-card>
</template>

<script>
import { mdiInvoiceTextCheckOutline } from '@mdi/js'

export default {
  name: 'BookingSummaryCard',
  props: {
    selectedCategory: {
      type: Object,
      default: null,
    },
    searchParams: {
      type: Object,
      required: true,
    },
    paymentMethod: {
      type: String,
      default: 'pix',
    },
    selectedInstallments: {
      type: Number,
      default: 1,
    },
    installmentValue: {
      type: Number,
      default: 0,
    },
    holdActive: Boolean,
    holdCountdownLabel: {
      type: String,
      default: '--:--',
    },
    holdExpired: Boolean,
    holdToken: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      summaryIcon: mdiInvoiceTextCheckOutline,
    }
  },
  computed: {
    totalPrice() {
      return this.selectedCategory ? this.selectedCategory.total_price : 0
    },
    formattedCheckIn() {
      return this.formatDisplayDate(this.searchParams.check_in)
    },
    formattedCheckOut() {
      return this.formatDisplayDate(this.searchParams.check_out)
    },
    guestsLabel() {
      const adults = Number(this.searchParams.adults || 0)
      const children = Number(this.searchParams.children || 0)
      const adultLabel = adults === 1 ? '1 adulto' : `${adults} adultos`
      const childLabel = children === 1 ? '1 criança' : `${children} crianças`

      return `${adultLabel} · ${childLabel}`
    },
    nightsCount() {
      if (this.selectedCategory && Number(this.selectedCategory.days)) {
        return Number(this.selectedCategory.days)
      }
      const { check_in: checkIn, check_out: checkOut } = this.searchParams || {}
      if (!checkIn || !checkOut) {
        return 0
      }
      const start = new Date(checkIn)
      const end = new Date(checkOut)
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return 0
      }
      const diff = Math.round((end.getTime() - start.getTime()) / 86400000)

      return diff > 0 ? diff : 0
    },
    nightsLabel() {
      const nights = this.nightsCount
      if (!nights) {
        return 'Defina as datas'
      }

      return nights === 1 ? '1 noite' : `${nights} noites`
    },
    advancePaymentLabel() {
      if (!this.selectedCategory) {
        return ''
      }
      if (this.selectedCategory.no_show_guarantee) {
        return 'Garantia No-Show (1 diária)'
      }

      return 'Pagamento antecipado necessário'
    },
  },
  methods: {
    formatCurrency(value) {
      if (value === null || value === undefined || value === '') {
        return 'R$ 0,00'
      }
      const number = Number(value)

      return number.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    },
    formatDisplayDate(value) {
      if (!value) return '--/--/----'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return '--/--/----'
      }
      const formatter = new Intl.DateTimeFormat('pt-BR', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        timeZone: 'UTC',
      })
      const formatted = formatter.format(date)

      return formatted.charAt(0).toUpperCase() + formatted.slice(1)
    },
  },
}
</script>

<style scoped>
.booking-summary {
  padding-bottom: 16px;
}

.booking-summary__title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.booking-summary__title-text span {
  display: block;
  line-height: 1.2;
}

.booking-summary__eyebrow {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--text-muted);
  margin-bottom: 4px;
}

.booking-summary__avatar {
  width: 44px;
  height: 44px;
  border-radius: 14px !important;
  background: rgba(25, 118, 210, 0.08);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  padding: 6px;
  color: var(--hero-start);
  display: flex;
  align-items: center;
  justify-content: center;
}

.booking-summary__avatar svg {
  width: 24px;
  height: 24px;
}

.booking-summary__body {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.booking-summary__total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  background: linear-gradient(135deg, var(--card-start), var(--card-end));
  border-radius: 18px;
  padding: 16px;
  border: 1px solid rgba(15, 76, 129, 0.08);
}

.booking-summary__amount {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--text-primary);
}

.booking-summary__amount--small {
  font-size: 1.1rem;
}

.booking-summary__chip {
  background-color: var(--bg-top) !important;
  color: var(--hero-start) !important;
  border: 1px solid var(--outline-color) !important;
  font-weight: 600;
}

.booking-summary__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.booking-summary__item {
  border: 1px solid rgba(15, 76, 129, 0.08);
  border-radius: 16px;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.75);
  min-height: 90px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.summary-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 4px;
}

.summary-value {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
}

.booking-summary__notice {
  border-radius: 18px;
  padding: 16px;
  background: rgba(46, 125, 50, 0.08);
  border: 1px solid rgba(46, 125, 50, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.booking-summary__installments {
  display: flex;
  justify-content: flex-start;
}

.booking-summary__footer {
  padding: 0 16px 16px;
}
</style>
